// React Dependencies
import React, { useEffect } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import MaterialTable from 'material-table';
import { toast } from 'react-toastify';

// Internal Dependencies
import MText from '../../../../../components/Common/MText';
import LoyaltyMenuDescriptionBox from '../../../../../components/LoyaltyMenuDescriptionBox';
import Colours from '../../../../../styles/colours';
import withLoyaltyConnect from '../WithLoyaltyConnect';
import { IsEmpty } from '../../../../../common/util';
import MButton from '../../../../../components/MButton';
import ApiService from '../../../../../services/apiService';
import { Status } from '../../../../../common/constants';

const Outlet = (props) => {
  const isModifiable = props.loyaltyProgram.status !== Status.Deactivated;
  const isLive = props.loyaltyProgram.status === Status.Live;

  const handleParticipatingOutletsChange = async (participateOutlets) => {
    try {
      props.dispatchStartSavingLC();

      await ApiService.updateLoyaltyCardParticipateOutlets({
        pageId:        props.page.id,
        loyaltyCardId: props.match?.params?.loyaltyCardId,
        outletIds:     participateOutlets.map((o) => o.id),
      });

      props.updateLoyaltyCardAction({ participateOutlets });
      toast.success('Changes saved');
    } catch (error) {
      toast.error(error?.message || 'Failed to update participate outlets');
    } finally {
      props.dispatchFinishSavingLC();
    }
  };

  const handleRemoveParticipateOutlet = (outlet) => {
    const filteredPO = props.loyaltyProgram.participateOutlets.filter((po) => po.id !== outlet.id);
    handleParticipatingOutletsChange(filteredPO);
  };

  const handleAddParticipateOutlet = (outlet) => {
    const updatedParticipateOutlets = [...props.loyaltyProgram.participateOutlets, outlet];
    handleParticipatingOutletsChange(updatedParticipateOutlets);
  };

  const getAvailableOutlets = () => {
    const participateIds = new Set(props.loyaltyProgram.participateOutlets.map((po) => po.id));
    return props.loyaltyProgram.outlets.filter((outlet) => !participateIds.has(outlet.id));
  };

  const getTableHeader = (actionCol) => [
    { title: 'Name', field: 'name', editable: 'never', sorting: false },
    { title: 'Address', field: 'address', editable: 'never', sorting: false },
    { title: 'Postal Code', field: 'postalCode', editable: 'never', sorting: false },
    { title: 'Unit No', field: 'unitNo', editable: 'never', sorting: false },
    { title: '', render: actionCol },
  ];

  useEffect(() => {
    props.hideSaveBtnAction();
    return () => {
      props.showSaveBtnAction();
    };
  }, []);

  return (
    <div className="container-fluid px-0 pt-3 mb-5">
      <div className="row px-0">
        <div className="col-12 col-xl-8">
          <div className="row px-0">
            <div className="col-12 col-xl-8">
              <LoyaltyMenuDescriptionBox
                bold={false}
                text="Select the outlet(s) participating in the loyalty program."
              />
            </div>
          </div>
          <div className="mt-4">
            <MText bold text="Available Outlet(s)" size={13} />
            {
              IsEmpty(props.loyaltyProgram.outlets) ? (
                !props.savingLC ? (
                  <MText className="mt-3">
                    You do not have any outlet. Please click
                    {' '}
                    <a
                      style={{ color: Colours.Teal, cursor: 'pointer', borderBottom: `1px solid ${Colours.Teal}` }}
                      href={`/page/${props.page.id}/outlets`}
                    >
                      here
                    </a>
                    {' '}
                    to add outlet.
                  </MText>
                ) : null
              ) : (
                <MaterialTable
                  title=""
                  style={{ marginTop: 16, fontSize: 12 }}
                  columns={getTableHeader((row) => (
                    <div className="w-100 d-flex align-items-center">
                      <MButton
                        disableElevation
                        style={{
                          background:   Colours.LightGray2,
                          borderRadius: 0,
                        }}
                        disabled={!isModifiable}
                        text="+ Add"
                        textStyle={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
                        type="button"
                        onClick={() => handleAddParticipateOutlet(row)}
                      />
                    </div>
                  ))}
                  data={getAvailableOutlets()}
                  options={{
                    actionsColumnIndex:         -1,
                    filtering:                  false,
                    showEmptyDataSourceMessage: false,
                    emptyRowsWhenPaging:        false,
                    headerStyle:                { fontSize: 12 },
                    searchFieldStyle:           { fontSize: 12 },
                  }}
                />
              )
            }
          </div>
          <div className="mt-5">
            <MText bold text="Participating Outlet(s)" size={13} />
            <MaterialTable
              title=""
              style={{ marginTop: 16, fontSize: 12 }}
              columns={getTableHeader((row) => (
                <div className="w-100 d-flex align-items-center">
                  <MButton
                    disableElevation
                    style={{
                      background:   Colours.LightGray2,
                      borderRadius: 0,
                    }}
                    disabled={!isModifiable || (isLive && props.loyaltyProgram.participateOutlets.length <= 1)}
                    text="- Remove"
                    textStyle={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
                    type="button"
                    onClick={() => handleRemoveParticipateOutlet(row)}
                  />
                </div>
              ))}
              data={_.orderBy(props.loyaltyProgram.participateOutlets, 'id', 'asc')}
              options={{
                actionsColumnIndex:         -1,
                filtering:                  false,
                showEmptyDataSourceMessage: false,
                emptyRowsWhenPaging:        false,
                headerStyle:                { fontSize: 12 },
                searchFieldStyle:           { fontSize: 12 },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

Outlet.propTypes = {
  loyaltyProgram: PropTypes.shape({
    status:  PropTypes.oneOf([Status.Draft, Status.Activated, Status.Deactivated]),
    outlets: PropTypes.arrayOf(PropTypes.shape({
      id:         PropTypes.number,
      name:       PropTypes.string,
      address:    PropTypes.string,
      postalCode: PropTypes.string,
      unitNo:     PropTypes.string,
    })),
    participateOutlets: PropTypes.arrayOf(PropTypes.shape({
      id:         PropTypes.number,
      name:       PropTypes.string,
      address:    PropTypes.string,
      postalCode: PropTypes.string,
      unitNo:     PropTypes.string,
    })),
  }).isRequired,
  page: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      loyaltyCardId: PropTypes.number,
    }),
  }).isRequired,
  savingLC: PropTypes.bool.isRequired,

  updateLoyaltyCardAction: PropTypes.func.isRequired,
  dispatchStartSavingLC:   PropTypes.func.isRequired,
  dispatchFinishSavingLC:  PropTypes.func.isRequired,
  hideSaveBtnAction:       PropTypes.func.isRequired,
  showSaveBtnAction:       PropTypes.func.isRequired,
};

export default withLoyaltyConnect(Outlet);
